import {
  FAIL_STANCER_STATUS,
  SHORT_UNIT,
  SUCCESS_STANCER_STATUS
} from 'const/Const';
import {
  addMonths,
  endOfYear,
  format,
  getDaysInMonth,
  lastDayOfMonth,
  startOfYear
} from 'date-fns';
import jwt_decode from 'jwt-decode';
import type { InvoiceType } from 'types/InvoicesList';
import type {
  FailStatus,
  Merchant,
  SubscriptionFees,
  SuccessStatus
} from 'types/Merchant';

interface IDecodeJwt {
  exp: number;
  iat: number;
  merchant_id: number;
  merchant_slug: string;
  roles: string[];
  username: string;
}

export const formatDateMonthYear = 'dd/MM/yyyy';

export const MONTHS: { [key: string]: number } = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12
};

export const MONTHSINFRENCH: string[] = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre'
];

export const MONTHS_IN_FRENCH_SHORT: string[] = [
  'Janv.',
  'Févr.',
  'Mars',
  'Avr.',
  'Mai',
  'Juin',
  'Juill.',
  'Août',
  'Sept.',
  'Oct.',
  'Nov.',
  'Déc.'
];

export const decodeJwt = (token: string, option?: { [key: string]: any }) => {
  return jwt_decode<IDecodeJwt>(token, option);
};

export { handleError, handleSuccess } from './handleResponse';

export const replaceCharacter = (
  str: string,
  regex: string,
  newStr: string
) => {
  if (!str.includes('.') && !str.includes(',')) {
    str = str + '.00';
  }
  return str.replace(new RegExp(regex, 'g'), newStr);
};

export const compare = (
  arr: any,
  type: 'increase' | 'decrease',
  sortByField?: string
) => {
  return arr.sort((a: any, b: any) => {
    if (typeof a === 'number' && typeof b === 'number') {
      if (type === 'increase') {
        return a - b;
      } else {
        return b - a;
      }
    }
    if (typeof a === 'string' && typeof b === 'string') {
      if (type === 'increase') {
        return a.localeCompare(b);
      } else {
        return b.localeCompare(a);
      }
    }
    if (typeof a === 'object' && typeof b === 'object') {
      if (!sortByField) return;

      if (
        typeof a[sortByField] === 'number' &&
        typeof b[sortByField] === 'number'
      ) {
        if (type === 'increase') {
          return a[sortByField] - b[sortByField];
        } else {
          return b[sortByField] - a[sortByField];
        }
      }

      if (
        typeof a[sortByField] === 'string' &&
        typeof b[sortByField] === 'string'
      ) {
        if (type === 'increase') {
          return a[sortByField].localeCompare(b[sortByField]);
        } else {
          return b[sortByField].localeCompare(a[sortByField]);
        }
      }
    }
  });
};

const createImage = (url: string) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

export async function getCroppedImg(imageSrc: string, pixelCrop: any) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx: any = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // As Base64 string
  return canvas.toDataURL('image/jpeg');
}

export function formatThousand(n: number) {
  if (String(n).length <= 3) {
    return `${n},000`;
  }
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function convertHMS(second: number) {
  const sec = parseInt(String(second), 10);
  let hours: number | string = Math.floor(sec / 3600);
  let minutes: number | string = Math.floor((sec - hours * 3600) / 60);
  let seconds: number | string = sec - hours * 3600 - minutes * 60;

  if (hours < 10) {
    hours = '0' + hours;
  }
  if (minutes < 10) {
    minutes = '0' + minutes;
  }
  if (seconds < 10) {
    seconds = '0' + seconds;
  }

  if (hours === '00' && minutes === '00') {
    return `${seconds}s`;
  }

  if (hours === '00') {
    return `${minutes}m ${seconds}s`;
  }

  return `${hours}h ${minutes}m ${seconds}s`;
}

export function periodWeekly() {
  const currentDay = new Date();
  const day = currentDay.getDay() || 7;
  const first = currentDay.getDate() - day + 1;
  const last = first + 6;
  const firstDayThisWeek = new Date(currentDay.setDate(first));
  const lastDayThisWeek = new Date(new Date().setDate(last)); // apply for 2 month different

  return [firstDayThisWeek, lastDayThisWeek];
}

export function formatDollar(price: number) {
  return replaceCharacter(String((price / 100).toFixed(2)), '[.]', ',');
}

export function replaceDotToComma(data: string | number) {
  if (!data) return '';
  return data.toString().replace('.', ',');
}

export function renderTypeString(type: InvoiceType) {
  switch (type) {
    case `App\\Entity\\Invoice\\CustomerInvoice`:
      return `Commerçant pour le client`;
    case `App\\Entity\\Invoice\\MonthlyInvoice`:
      return `Facture d'abonnement Achil`;
    case `App\\Entity\\Invoice\\WeeklyInvoice`:
      return `Frais de service Achil`;
    case `App\\Entity\\SubscriptionPlan\\MerchantSubscriptionPlan`:
      return `Abonnement Achil`;
    default:
      break;
  }
}

export function getInvoiceType(type: InvoiceType) {
  switch (type) {
    case `App\\Entity\\Invoice\\CustomerInvoice`:
      return `customer_invoice`;
    case `App\\Entity\\Invoice\\MonthlyInvoice`:
      return `merchant_subscription_invoice`;
    case `App\\Entity\\Invoice\\WeeklyInvoice`:
      return `merchant_operation_invoice`;
    case `App\\Entity\\SubscriptionPlan\\MerchantSubscriptionPlan`:
      return `accounting_report_invoices`;
    default:
      break;
  }
}

export function downLoadPdfFile(dataPdf: any, nameFile: any) {
  const blob = new Blob([dataPdf], {
    type: 'application/pdf;charset=utf-8'
  });
  const fileURL = URL.createObjectURL(blob);
  const downloadLink = document.createElement('a');
  downloadLink.href = fileURL;
  downloadLink.download = nameFile;
  downloadLink.click();
}

export function downLoadFileNameInvoices(
  type: ReturnType<typeof getInvoiceType>,
  id: string
) {
  switch (type) {
    case 'customer_invoice':
      return `Achil_Customer_invoice_${id}.pdf`;
    case 'merchant_subscription_invoice':
      return `Achil_Subscription_fee_${id}.pdf`;
    case 'merchant_operation_invoice':
      return `Achil_Operation_cost_${id}.pdf`;
    case 'accounting_report_invoices':
      return `Standard_Plan_invoice_${id}.pdf`;
    default:
      break;
  }
}

export function downLoadFileNameFinancialReport(
  startDate: string,
  endDate: string
) {
  return `Financial_Report_from_${startDate}_to_${endDate}.pdf`;
}

export function getDateInMonth(year: number, month: number) {
  return getDaysInMonth(new Date(year, month - 1));
}

export function formatDayOfWeek(day: string | number) {
  switch (day) {
    case typeof day === 'string' ? 'Mon' : 0:
      return 'Lundi';
    case typeof day === 'string' ? 'Tue' : 1:
      return 'Mardi';
    case typeof day === 'string' ? 'Wed' : 2:
      return 'Mercredi';
    case typeof day === 'string' ? 'Thu' : 3:
      return 'Jeudi';
    case typeof day === 'string' ? 'Fri' : 4:
      return 'Vendredi';
    case typeof day === 'string' ? 'Sat' : 5:
      return 'Samedi';
    case typeof day === 'string' ? 'Sun' : 6:
      return 'Dimanche';
    default:
      return '';
  }
}

export function removeEleDuplicate(arr: any, keyNeedRemove: string) {
  const result: any = [];
  arr.map((item: any) => {
    const check = result.some(
      (itemCheck: any) => itemCheck[keyNeedRemove] === item[keyNeedRemove]
    );
    if (!check) {
      result.push(item);
    }
    return item;
  });
  return result;
}

export function formatPercent(price: string, toFixedNumber = 2) {
  if (!price.includes('.') && !price.includes(',')) {
    return price;
  }
  const formatPrice = Number(price).toFixed(toFixedNumber);
  return String(formatPrice).replace('.', ',');
}

export function convertTZ(date: string | Date, tzString: string) {
  return new Date(
    (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', {
      timeZone: tzString
    })
  );
}

export function getTimeZoneRegion() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function compareSort(
  arr: any,
  type: 'increase' | 'decrease',
  fieldSort: string
) {
  return arr.sort((a: any, b: any) => {
    if (type === 'increase') {
      return a[fieldSort].localeCompare(b[fieldSort]);
    }
    if (type === 'decrease') {
      return b[fieldSort].localeCompare(a[fieldSort]);
    }
  });
}

export default function getInfoPageParams(currentURI: string, lastURI: string) {
  if (currentURI.indexOf('?') === -1 || lastURI.indexOf('?') === -1) return;
  const currentURIQueryString = currentURI.slice(
    currentURI.indexOf('?') + 1,
    currentURI.length
  );
  const lastURIQueryString = lastURI.slice(
    lastURI.indexOf('?') + 1,
    lastURI.length
  );
  const currentPage = getPageWithSearchParams(currentURIQueryString);
  const lastPage = getPageWithSearchParams(lastURIQueryString);
  return { currentPage, lastPage };
}

function getPageWithSearchParams(queryString: string) {
  const searchParams = new URLSearchParams(queryString);
  return Number(searchParams.get('page'));
}

export function isFailStatus(status: any): status is FailStatus {
  return FAIL_STANCER_STATUS.includes(status);
}
export function isSuccessStatus(status: any): status is SuccessStatus {
  return SUCCESS_STANCER_STATUS.includes(status);
}

export function generateQueryString(params: Record<string, any>) {
  if (!params || typeof params !== 'object') {
    throw new Error('Input must be an object');
  }

  const queryString = Object.entries(params)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map(
            (val) => `${encodeURIComponent(key)}[]=${encodeURIComponent(val)}`
          )
          .join('&');
      }

      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .filter(Boolean)
    .join('&');

  return `?${queryString}`;
}

export function getDateFirstLastMonth(year: number, type: 'first' | 'last') {
  if (type === 'first') {
    return `${year}-01-01`;
  } else {
    return `${year}-12-31`;
  }
}

export function transformDateToISO(dirtyDate: Date | string) {
  return format(new Date(dirtyDate), 'yyyy-MM-dd');
}

export function getYearRange(year: number) {
  return {
    startDate: startOfYear(new Date(year, 0, 1)),
    endDate: endOfYear(new Date(year, 11, 31))
  };
}

export function getNextRenewalDate(
  anchorDirtyDate: string | Date,
  formatStr = 'dd/MM/yyyy'
) {
  const anchorDate = new Date(anchorDirtyDate);

  const nextMonthDate = addMonths(anchorDate, 1); // Add 1 month to the given date
  const lastDay = lastDayOfMonth(nextMonthDate); // Get the last day of the new month

  // If the original day is greater than the last day of the next month, adjust it
  const renewalDate =
    anchorDate.getDate() > lastDay.getDate()
      ? lastDay
      : new Date(nextMonthDate.setDate(anchorDate.getDate()));

  return format(renewalDate, formatStr);
}

export function getOrderDateAndTime(dateAndTime?: string | Date) {
  if (!dateAndTime) {
    return { date: '--/--/----', time: '--:--' };
  }

  const region = getTimeZoneRegion();
  const newDateTimeZone = convertTZ(dateAndTime, region);

  return {
    date: format(newDateTimeZone, formatDateMonthYear),
    time: format(newDateTimeZone, 'HH:mm')
  } as const;
}

export function renderQuantityWithUnit(
  quantity: number,
  quantityProductSoldByWeight: number,
  unit: keyof typeof SHORT_UNIT
) {
  const price = Number(quantity * quantityProductSoldByWeight)
    .toFixed(3)
    .replace(/\.?0+$/, '');

  return price + SHORT_UNIT[unit];
}

export function getPreferPlan(
  localPlan: Partial<SubscriptionFees>,
  serverPlan: Merchant['subscription'] | undefined
) {
  if (
    !!Object.keys(localPlan).length &&
    serverPlan &&
    serverPlan.type !== localPlan.type
  ) {
    return localPlan;
  }

  return serverPlan ?? localPlan;
}

export function calculateTax(
  total: number | string,
  taxRate = 0.2
): {
  beforeTax: string;
  taxAmount: string;
} {
  let n: number;
  if (typeof total === 'number') {
    n = total;
  } else {
    const cleaned = String(total)
      .trim()
      .replace(/\s+/g, '')
      .replace(
        /^([0-9.]+),([0-9]+)$/,
        (_, intPart, decPart) => intPart.replace(/\./g, '') + '.' + decPart
      )
      .replace(/,/g, '.');

    n = Number(cleaned);
  }

  if (!isFinite(n) || Number.isNaN(n)) {
    return {
      beforeTax: '0,00',
      taxAmount: '0,00'
    };
  }

  const beforeTaxRaw = n / (1 + taxRate);

  const taxRaw = n - beforeTaxRaw;

  const beforeTaxNumber = Math.round(beforeTaxRaw * 100) / 100;
  const taxAmountNumber = Math.round(taxRaw * 100) / 100;

  const fmt = (v: number) => v.toFixed(2).replace('.', ',');

  return {
    beforeTax: fmt(beforeTaxNumber),
    taxAmount: fmt(taxAmountNumber)
  };
}

export function toPercentage(value: number, decimals = 2) {
  const clamped = Math.min(1, Math.max(0, value));
  const percent = clamped * 100;
  const factor = 10 ** decimals;

  // Rounded using integer math to avoid FP noise
  const rounded = Math.round(percent * factor) / factor;

  // Convert -0 to 0 (can happen with rounding of tiny negatives)
  return Object.is(rounded, -0) ? 0 : rounded;
}
